"use client";

import { paceReadback } from "@/lib/onboarding/copy";
import {
  DEFAULT_WEEKLY_SCENARIO_GOAL,
  WEEKLY_SCENARIO_GOAL_OPTIONS,
} from "@/lib/onboarding/constants";
import { cn } from "@/lib/utils";

import type { StepProps } from "../types";
import { ContinueButton, StepFooter, StepHeader } from "../ui";

/** Scenarios per week. Taps record the pace (`update`); the button advances
 *  and records the current value, so the default is saved even if the
 *  student never taps. The draft schema allows `""` for both answers, so
 *  empty values fall back to the default / no timeframe. */
export function PaceStep({ state, update, advance }: StepProps) {
  const saved = state.answers.weekly_scenario_goal;
  const current = typeof saved === "number" ? saved : DEFAULT_WEEKLY_SCENARIO_GOAL;
  const timeframe = state.answers.exam_timeframe || undefined;

  return (
    <>
      <StepHeader
        kicker="Your pace"
        title="How many scenarios a week?"
        body="Pick a pace you can keep up."
      />
      <div className="mt-7">
        <div
          role="group"
          aria-label="Scenarios per week"
          className="grid grid-cols-4 gap-2"
        >
          {WEEKLY_SCENARIO_GOAL_OPTIONS.map((value) => {
            const selected = current === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={selected}
                onClick={() => update({ weekly_scenario_goal: value })}
                className={cn(
                  "h-16 rounded-xl border transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50",
                  selected
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-card text-muted-foreground hover:bg-muted/60",
                )}
              >
                <span className="font-mono text-xl font-semibold">{value}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-center text-sm text-primary">
          Recommended: {DEFAULT_WEEKLY_SCENARIO_GOAL} a week
        </p>
      </div>
      <div aria-live="polite" className="mt-6">
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-[15px] text-foreground">
            {paceReadback(current, timeframe)}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Estimate, not a promise.
          </p>
        </div>
      </div>
      <StepFooter>
        <ContinueButton
          onClick={() => advance({ weekly_scenario_goal: current })}
        >
          Build my plan
        </ContinueButton>
      </StepFooter>
    </>
  );
}
